/**
 * TwoFactorSetupModal Component
 * 
 * Modal for setting up two-factor authentication with QR code
 * 
 * Features:
 * - Modal overlay with focus trap
 * - QR code display for authenticator app scanning
 * - 6-digit code verification
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { securityService } from '../../../services/security-service';
import { getCurrentUserId } from '../../../lib/supabase';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function TwoFactorSetupModal({ 
  isOpen, 
  onClose,
  onSuccess
}: TwoFactorSetupModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState<'qr' | 'verify'>('qr');
  const [qrCode, setQrCode] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  // Initialize 2FA setup when modal opens
  useEffect(() => {
    if (isOpen && step === 'qr') {
      initiateSetup();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('qr');
      setQrCode('');
      setFactorId('');
      setVerificationCode('');
      setError('');
      setBackupCodes([]);
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loading, onClose]);

  // Focus code input when verification step starts
  useEffect(() => {
    if (step === 'verify' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const initiateSetup = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to enable 2FA');
        return;
      }

      const response = await securityService.initiate2FASetup(userId);
      
      if (response.error) {
        setError(response.error.message || 'Failed to initiate 2FA setup');
        return;
      }

      if (response.data) {
        setQrCode(response.data.qrCode);
        setFactorId(response.data.factorId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initiate 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to verify 2FA');
        return;
      }

      const response = await securityService.verify2FACode(userId, factorId, verificationCode);
      
      if (response.error) {
        setError(response.error.message || 'Invalid verification code');
        return;
      }

      if (response.data?.backupCodes && response.data.backupCodes.length > 0) {
        setBackupCodes(response.data.backupCodes);
      }

      // 2FA setup complete - close modal and refresh settings
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="2fa-setup-title"
      aria-describedby="2fa-setup-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
                <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div>
                <h2 id="2fa-setup-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Enable Two-Factor Authentication
                </h2>
                <p id="2fa-setup-description" className="text-sm text-gray-600 dark:text-gray-400">
                  {step === 'qr' ? 'Scan QR code with your authenticator app' : 'Enter verification code'}
                </p>
              </div>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {step === 'qr' ? (
              <div className="space-y-4">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      Generating QR code...
                    </p>
                  </div>
                ) : qrCode ? (
                  <>
                    <div className="flex flex-col items-center space-y-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                        Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                      </p>
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700">
                        <img 
                          src={qrCode} 
                          alt="QR code for 2FA setup" 
                          className="w-64 h-64"
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        After scanning, click Continue to enter the 6-digit code
                      </p>
                    </div>
                    {error && (
                      <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end space-x-3 mt-6">
                      <button 
                        type="button" 
                        onClick={onClose} 
                        className="btn-secondary"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setStep('verify')}
                        className="btn-primary"
                        disabled={loading}
                      >
                        Continue
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                    <p className="mt-4 text-sm text-red-600 dark:text-red-400">
                      {error || 'Failed to generate QR code'}
                    </p>
                    <button 
                      type="button" 
                      onClick={initiateSetup}
                      className="btn-secondary mt-4"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label 
                    htmlFor="verification-code" 
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Enter 6-digit code from your authenticator app
                  </label>
                  <input
                    ref={codeInputRef}
                    type="text"
                    id="verification-code"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                      setError('');
                    }}
                    className={`input w-full text-center text-2xl tracking-widest ${error ? 'border-red-500' : ''}`}
                    placeholder="000000"
                    maxLength={6}
                    required
                    aria-invalid={!!error}
                    aria-describedby={error ? 'verification-code-error' : undefined}
                    autoComplete="off"
                  />
                  {error && (
                    <p id="verification-code-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {error}
                    </p>
                  )}
                </div>

                {backupCodes.length > 0 && (
                  <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        2FA enabled successfully!
                      </p>
                    </div>
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Backup codes have been generated. Please save them securely.
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <button 
                    type="button" 
                    onClick={() => setStep('qr')}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={loading || verificationCode.length !== 6}
                    data-loading={loading ? 'true' : undefined}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Enable'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

